import { useQuery } from '@tanstack/react-query'
import { secondsToMilliseconds } from 'date-fns'
import { toContextTuplesString } from 'utils/logging'

async function fetchCoingeckoUsdPrices(
  inputCoingeckoId: string,
  outputCoingeckoId: string,
) {
  const url = new URL('https://api.coingecko.com/api/v3/simple/price')
  const params = new URLSearchParams({
    ids: `${inputCoingeckoId},${outputCoingeckoId}`,
    vs_currencies: 'usd',
  })
  url.search = params.toString()

  try {
    const results = await fetch(url)
    if (!results.ok) {
      const logLevel = results.status === 429 ? 'info' : 'error'
      console[logLevel](
        `Error loading coingecko prices: ${toContextTuplesString({
          status: results.status,
          inputId: inputCoingeckoId,
          outputId: outputCoingeckoId,
        })}`,
      )
      return null
    }
    return (await results.json()) as Record<string, { usd: number }>
  } catch (e) {
    console.error(
      `Error loading coingecko prices: ${toContextTuplesString({
        inputId: inputCoingeckoId,
        outputId: outputCoingeckoId,
      })}`,
      e,
    )
    return null
  }
}

export function useCoingeckoPairUsdPrices(
  inputCoingeckoId: string | null | undefined,
  outputCoingeckoId: string | null | undefined,
) {
  const { data: prices, isFetching } = useQuery({
    queryKey: ['coingecko-pair-prices', inputCoingeckoId, outputCoingeckoId],
    queryFn: async () => {
      if (!inputCoingeckoId || !outputCoingeckoId) return null

      const prices = await fetchCoingeckoUsdPrices(
        inputCoingeckoId,
        outputCoingeckoId,
      )
      const coingeckoInputPrice = prices?.[inputCoingeckoId].usd
      const coingeckoOutputPrice = prices?.[outputCoingeckoId].usd
      if (!coingeckoInputPrice || !coingeckoOutputPrice) {
        return null
      }

      return {
        [inputCoingeckoId]: coingeckoInputPrice,
        [outputCoingeckoId]: coingeckoOutputPrice,
      }
    },
    cacheTime: secondsToMilliseconds(10),
    staleTime: secondsToMilliseconds(10),
    refetchOnWindowFocus: false,
    enabled: !!(inputCoingeckoId && outputCoingeckoId),
  })

  return {
    prices,
    loading: isFetching,
  }
}
